import {
    Box,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    Select,
    MenuItem,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableSortLabel,
    TableBody,
    Popover,
    Divider,
    Button,
    Snackbar
} from '@mui/material';
import React, { useEffect, useReducer, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import SearchIcon from '@mui/icons-material/SearchOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ShareIcon from '@mui/icons-material/Share';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';

import GameService from '../../../services/game.service';
import { ActionData, MenuProps, isGoalTag } from '../components/common';
import { getComparator, stableSort, enrichPlayerData } from '../components/utilities';
import { PLAYER_ICON_DEFAULT } from '../../../common/staticData';
import PlayerEditDialog from './playerEditDialog';
import PlayerStatDialog from '../leaders/status';
import GameExportToEdits from '../games/tabs/overview/exportEdits';
import PlayersGamesDialog from './gamesDialog';
import TeamStatsVideoPlayer from '../teams/tabs/stats/videoDialog';
import { getPeriod } from '../games/tabs/overview/tagListItem';
import SeasonContext from '../../../common/Context';
import { setAllPlayersStatsUnified } from '../../../helpers/coachSlice';
import CryptoJS from 'crypto-js';
import CloseIcon from '@mui/icons-material/Close';

const headCells = [
    { id: 'total_player_games', title: 'Games', action: '' },
    { id: 'total_goals', title: 'Goals', action: 'Goal' },
    { id: 'total_assists', title: 'Assists', action: 'Assist' },
    { id: 'total_shots', title: 'Shots', action: 'Shot' },
    { id: 'total_shots_on_target', title: 'Shots on Target', action: 'ShotOnTarget' },
    { id: 'total_passes', title: 'Passes', action: 'Pass' },
    { id: 'pass_success_rate', title: 'Pass %', action: '' },
    { id: 'total_dribbles', title: 'Dribbles', action: 'Dribble' },
    { id: 'total_tackles', title: 'Tackles', action: 'Tackle' },
    { id: 'total_interceptions', title: 'Interceptions', action: 'Interception' },
    { id: 'total_crosses', title: 'Crosses', action: 'Cross' },
    { id: 'total_corners', title: 'Corners', action: 'Corner' },
    { id: 'total_free_kicks', title: 'Free Kicks', action: 'FreeKick' },
    { id: 'total_clearances', title: 'Clearances', action: 'Clearance' },
    { id: 'total_draw_fouls', title: 'Draw Fouls', action: 'DrawFoul' },
    { id: 'total_fouls', title: 'Fouls', action: 'Foul' },
    { id: 'total_yellow_cards', title: 'Yellow Cards', action: 'YellowCard' }
];

const Players = ({ t }) => {
    const { seasonFilter } = React.useContext(SeasonContext);
    const [state, setState] = useReducer((old, action) => ({ ...old, ...action }), {
        searchText: '',
        teamFilter: 'none'
    });

    const { user: currentUser } = useSelector((state) => state.auth);
    const { searchText, teamFilter } = state;

    const teamsList = useSelector((state) => state.coach.teamsList);
    const gamesList = useSelector((state) => state.coach.gamesList);
    const allPlayersStatsUnified = useSelector((state) => state.coach.allPlayersStatsUnified);
    const dispatch = useDispatch();

    // Get players from unified API data for current season, excluding goalkeepers
    const playersList = React.useMemo(() => {
        if (!seasonFilter || !allPlayersStatsUnified) return [];
        const seasonKey = seasonFilter === 'none' ? 'all' : seasonFilter.name;
        const allPlayers = allPlayersStatsUnified[seasonKey] || [];

        // Filter out goalkeepers (position_id === 16) to show only field players
        const fieldPlayers = allPlayers.filter(player => player.position_id !== 16);

        // Map unified data fields to expected field names for Players page
        return fieldPlayers.map(player => ({
            ...player,
            player_latest_team_play: player.team_name, // Map team_name to expected field
            id: player.player_id // Ensure id is set
        }));
    }, [seasonFilter, allPlayersStatsUnified]);

    const [seasonTeamsList, setSeasonTeamsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('player_full_name');
    const [filteredPlayers, setFilteredPlayers] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const [editPlayer, setEditPlayer] = useState(null);
    const [statOpen, setStatOpen] = useState(false);
    const [playerStat, setPlayerStat] = useState(null);
    const [playData, setPlayData] = useState([]);
    const [gameList, setGameList] = useState([]);
    const [exportOpen, setExportOpen] = useState(false);
    const [playerGames, setPlayerGames] = useState([]);
    const [gamesOpen, setGamesOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const menuPopoverOpen = Boolean(menuAnchorEl);
    const menuPopoverId = menuPopoverOpen ? 'simple-popover' : undefined;
    const [showResultAlert, setShowResultAlert] = useState(false);
    const [isShowShareIcon, setIsShowShareIcon] = useState('');
    const [editLoading, setEditLoading] = useState(false);
    const [videoOpen, setVideoOpen] = useState(false);
    const [videoData, setVideoData] = useState([]);
    const [videoGameList, setVideoGameList] = useState([]);

    const handleRequestSort = (prop) => {
        const isAsc = orderBy === prop && order === 'desc';

        setOrder(isAsc ? 'asc' : 'desc');
        setOrderBy(prop);
    };

    const handleShowMenu = (player) => (e) => {
        // Find team from teamsList for fallback data
        const playerTeam = teamsList.find((team) => team.team_name === player.team_name);

        // Use shared utility for consistent player data enrichment
        setPlayerStat(enrichPlayerData(player, playerTeam));
        setMenuAnchorEl(e.currentTarget);
    };

    // Open video player when clicking on a stat cell
    const handleDisplayStatDialogFromCell = (cell, player) => async (e) => {
        // Skip if it's the Games column (no action) or if the value is 0
        if (cell.action === '' || player[cell.id] === undefined || player[cell.id] <= 0) return;
        if (!seasonFilter) return;

        // Get ALL teams this player has played for (player may play for multiple teams)
        let playerTeamIds = [player.team_id];
        try {
            const teamsForPlayer = await GameService.getPlayerTeamForGames(player.player_id, null);
            if (teamsForPlayer && teamsForPlayer.length > 0) {
                playerTeamIds = teamsForPlayer.map(t => t.team_id);
            }
        } catch (err) {
            console.warn('Could not fetch all teams for player, using default team_id');
        }

        // Get game IDs for ALL teams this player has played for in the current season
        const player_games = gamesList.filter((game) =>
            game.season_id === seasonFilter.id &&
            (playerTeamIds.includes(game.home_team_id) || playerTeamIds.includes(game.away_team_id))
        );
        const gameIds = player_games.map((game) => game.id).join(',');

        // Format games for video player (array of {id, video_url})
        setVideoGameList(player_games.map((game) => ({ id: game.id, video_url: game.video_url || '' })));

        // Fetch tags for this action
        await GameService.getGamePlayerTags(
            currentUser.id,
            player.team_id,
            `${player.player_id}`,
            gameIds || null,
            ActionData[cell.action].action_id,
            ActionData[cell.action].action_type_id,
            ActionData[cell.action].action_result_id,
            null,
            null,
            null,
            null,
            null
        ).then((res) => {
            // Goal clips are shots only; assists are also tagged result=Goal, so filter them out.
            const tags = cell.action === 'Goal' ? res.filter(isGoalTag) : res;
            setVideoData(
                tags.map((item) => ({
                    tag_id: item.id,
                    start_time: item.player_tag_start_time,
                    end_time: item.player_tag_end_time,
                    player_name: item.player_names || item.player_name || item.full_name || '',
                    action_name: item.action_names || item.action_name || '',
                    action_type: item.action_type_names || item.action_type_name || item.action_type || '',
                    action_result: item.action_result_names || item.action_result_name || item.action_result || '',
                    game_id: item.game_id,
                    team_id: player.team_id,
                    court_area: item.court_area_id,
                    inside_pain: item.inside_the_pain,
                    period: item.period,
                    time_in_game: item.time_in_game,
                    home_team_logo: item.home_team_logo,
                    away_team_logo: item.away_team_logo,
                    home_team_goals: item.home_team_goals ?? item.home_team_goal,
                    away_team_goals: item.away_team_goals ?? item.away_team_goal
                }))
            );
            if (tags.length > 0) {
                setVideoOpen(true);
            }
        });
    };

    const handleExportPlayerTags = (cell, player) => async (e) => {
        e.preventDefault();

        if (!seasonFilter) return;
        if (cell.title !== 'Games' && player[cell.id] !== undefined && player[cell.id] > 0) {
            // Get ALL teams this player has played for (player may play for multiple teams)
            let playerTeamIds = [player.team_id];
            try {
                const teamsForPlayer = await GameService.getPlayerTeamForGames(player.player_id, null);
                if (teamsForPlayer && teamsForPlayer.length > 0) {
                    playerTeamIds = teamsForPlayer.map(t => t.team_id);
                }
            } catch (err) {
                console.warn('Could not fetch all teams for player, using default team_id');
            }

            // Get game IDs for ALL teams this player has played for in the current season
            const player_games = gamesList.filter((game) =>
                game.season_id === seasonFilter.id &&
                (playerTeamIds.includes(game.home_team_id) || playerTeamIds.includes(game.away_team_id))
            );
            const gameIds = player_games.map((game) => game.id).join(',');

            // Use null for team_id to get tags for all teams (player can play for multiple teams)
            await GameService.getGamePlayerTags(
                currentUser.id,
                player.team_id,
                `${player.player_id}`,
                gameIds || null, // Filter by season's games to match displayed stats
                ActionData[cell.action].action_id,
                ActionData[cell.action].action_type_id,
                ActionData[cell.action].action_result_id,
                null,
                null,
                null,
                null,
                null
            ).then((res) => {
                setPlayData(res);
                setExportOpen(true);
            });
        }
    };

    const handleDisplayGames = async () => {
        if (!seasonFilter) return;

        setMenuAnchorEl(null);
        // Query Players_Stats table directly - one row per game for this player
        await GameService.getPlayersStatsGamebyGame({
            seasonId: seasonFilter.id,
            leagueId: null,
            gameId: null, // Get all games for this player
            teamId: playerStat.team_id,
            playerId: playerStat.player_id,
            gameTime: null,
            courtAreaId: null,
            insidePaint: null,
            homeAway: null,
            gameResult: null
        }).then((res) => {
            setPlayerGames(stableSort(res, getComparator('desc', 'game_date')));
            setGamesOpen(true);
        });
    };

    // Open PlayerStatDialog from popover menu (same logic as Rankings page)
    const handleDisplayStats = async () => {
        if (!seasonFilter) return;
        setMenuAnchorEl(null);

        // Get ALL teams this player has played for (player may play for multiple teams)
        let playerTeamIds = [playerStat.team_id];
        try {
            const teamsForPlayer = await GameService.getPlayerTeamForGames(playerStat.player_id, null);
            if (teamsForPlayer && teamsForPlayer.length > 0) {
                playerTeamIds = teamsForPlayer.map(t => t.team_id);
            }
        } catch (err) {
            console.warn('Could not fetch all teams for player, using default team_id');
        }

        // Get games for ALL teams this player has played for in the current season
        const player_games = gamesList.filter((game) =>
            game.season_id === seasonFilter.id &&
            (playerTeamIds.includes(game.home_team_id) || playerTeamIds.includes(game.away_team_id))
        );
        setGameList(player_games);

        // Dialog uses statsData from Redux and initialState, no extra fetch needed (same as Rankings)
        setStatOpen(true);
    };

    const handleChange = (prop) => (e) => {
        setState({ [prop]: e.target.value });
    };

    const compareStrings = (first, last) => {
        return first.toLowerCase().includes(last.toLowerCase());
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const getPlayers = () => {
        let players = playersList || [];

        if (searchText) {
            players = players.filter(
                (item) =>
                    compareStrings(item.player_position_name || '', searchText) ||
                    compareStrings(item.player_full_name || '', searchText) ||
                    compareStrings(item.player_latest_team_play || '', searchText)
            );
        }

        if (teamFilter !== 'none') {
            players = players.filter((item) => item.player_latest_team_play === teamFilter.team_name);
        }

        return players;
    };

    const getSortedArray = () => {
        return stableSort(getPlayers(), getComparator(order, orderBy));
    };

    useEffect(() => {
        if (!currentUser.can_share) setIsShowShareIcon('hidden');
        else setIsShowShareIcon('');
    }, []);

    useEffect(() => {
        // Handle initial undefined state
        if (!playersList) {
            return;
        }

        let players = playersList || [];

        // Apply search filter
        if (searchText) {
            players = players.filter(
                (item) =>
                    compareStrings(item.player_position_name || '', searchText) ||
                    compareStrings(item.player_full_name || '', searchText) ||
                    compareStrings(item.player_latest_team_play || '', searchText)
            );
        }

        // Apply team filter
        if (teamFilter !== 'none') {
            players = players.filter((item) => item.player_latest_team_play === teamFilter.team_name);
        }

        // Sort players
        const sortedPlayers = stableSort(players, getComparator(order, orderBy));
        setFilteredPlayers(sortedPlayers);
        setLoading(false);
    }, [playersList, searchText, order, orderBy, teamFilter]);

    useEffect(() => {
        if (playersList && playersList.length) {
            // Extract unique teams from playersList
            const uniqueTeams = [...new Set(playersList.map((p) => p.player_latest_team_play))].filter(Boolean).map((teamName) => ({ team_name: teamName }));
            setSeasonTeamsList(uniqueTeams);
        } else if (playersList && playersList.length === 0) {
            setSeasonTeamsList([]);
            setLoading(false);
        }
    }, [playersList]);

    const encryptId = (id) => {
        const secretKey = 'scouting4u';
        const encrypted = CryptoJS.AES.encrypt(id.toString(), secretKey).toString();
        const encoded = encodeURIComponent(encrypted); // Encode encrypted data for URL safety
        return encoded;
    };

    const handleCopyLink = () => {
        navigator.clipboard
            .writeText(`${window.location.origin}/player/${encryptId(playerStat.player_id)}`)
            .then(() => {
                setShowResultAlert(true);
            })
            .catch((err) => {
                console.error('Failed to copy: ', err);
            });
        setMenuAnchorEl(null);
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setShowResultAlert(false);
    };

    // Fetch full player data before opening edit dialog
    const handleEditClick = async () => {
        const playerId = playerStat?.player_id || playerStat?.id;
        if (!playerId) {
            setEditPlayer(playerStat);
            setEditOpen(true);
            return;
        }
        setEditLoading(true);
        setMenuAnchorEl(null);
        try {
            const fullPlayerData = await GameService.getPlayerById(playerId);
            setEditPlayer(fullPlayerData);
            setEditOpen(true);
        } catch (error) {
            console.error('Error fetching player details:', error);
            // Fallback to opening with existing data
            setEditPlayer(playerStat);
            setEditOpen(true);
        } finally {
            setEditLoading(false);
        }
    };

    // Update player in Redux store after edit
    const updatePlayer = (newPlayer) => {
        if (!seasonFilter) return;
        const seasonKey = seasonFilter === 'none' ? 'all' : seasonFilter.name;
        if (allPlayersStatsUnified && allPlayersStatsUnified.hasOwnProperty(seasonKey)) {
            dispatch(
                setAllPlayersStatsUnified({
                    season_name: seasonKey,
                    data: allPlayersStatsUnified[seasonKey].map((player) => {
                        if (player.player_id === newPlayer.player_id) {
                            return { ...player, ...newPlayer };
                        }
                        return player;
                    })
                })
            );
        }
    };

    const action = (
        <React.Fragment>
            <Button color="secondary" size="small" onClick={handleClose}>
                UNDO
            </Button>
            <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </React.Fragment>
    );

    return (
        <Box sx={{ width: '98%', height: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ width: '100%', padding: '24px 24px 24px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p className="page-title">{t('All Players')}</p>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <p className="select-narrator">{t('Team')}</p>
                        <Select
                            value={teamFilter}
                            onChange={handleChange('teamFilter')}
                            label=""
                            variant="outlined"
                            IconComponent={ExpandMoreIcon}
                            inputProps={{ 'aria-label': 'Without label' }}
                            MenuProps={MenuProps}
                            sx={{ borderRadius: '10px', outline: 'none', height: '36px', width: '300px', fontSize: '0.8rem', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                        >
                            <MenuItem key="0" value="none">
                                {t('All')}
                            </MenuItem>
                            {seasonTeamsList &&
                                seasonTeamsList.map((team, index) => (
                                    <MenuItem key={index + 1} value={team}>
                                        {team.team_name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </Box>
                    <TextField
                        value={searchText}
                        onChange={handleChange('searchText')}
                        placeholder={t('Search')}
                        label=""
                        inputProps={{ 'aria-label': 'Without label' }}
                        variant="outlined"
                        sx={{
                            width: '300px',
                            fontSize: '0.8rem',
                            '& legend': { display: 'none' },
                            '& fieldset': { top: 0 },
                            '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                            '& .MuiOutlinedInput-input': { padding: 0, height: '36px' }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconButton
                                        onMouseDown={handleMouseDownPassword}
                                        sx={{ backgroundColor: '#F8F8F8', '&:hover': { backgroundColor: '#F8F8F8' }, '&:focus': { backgroundColor: '#F8F8F8' } }}
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
            </Box>

            {loading && (
                <div style={{ width: '100%', height: '100%', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </div>
            )}
            <Snackbar open={showResultAlert} autoHideDuration={3000} onClose={handleClose} message="Link copied to clipboard!" action={action} />

            {!loading && (
                <>
                    {filteredPlayers.length > 0 && (
                        <Box sx={{ flex: 1, marginLeft: '10px', background: '#ffffff', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                                <Table stickyHeader aria-label="sticky table" sx={{ tableLayout: 'fixed', width: '100%', minWidth: '1400px' }}>
                                    <TableHead>
                                        <TableRow height="36px">
                                            <TableCell key="image" sx={{ width: '64px' }} />
                                            <TableCell key="name" align="left" sx={{ width: '190px' }}>
                                                {t('Name')}
                                            </TableCell>
                                            {headCells.map((cell) => (
                                                <TableCell key={cell.id} align="center" sx={{ px: '4px' }} sortDirection={orderBy === cell.id ? order : false}>
                                                    <TableSortLabel active={orderBy === cell.id} direction={orderBy === cell.id ? order : 'asc'} onClick={() => handleRequestSort(cell.id)}>
                                                        {t(cell.title)}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredPlayers.map((player, index) => (
                                            <TableRow key={`${player.player_id}-${index}`} height="70px" hover>
                                                <TableCell key={`${player.player_id}-${index}-0`} align="center" sx={{ width: '64px', cursor: 'pointer' }} onClick={handleShowMenu(player)}>
                                                    <img
                                                        style={{ height: '70px', borderRadius: '8px', paddingTop: '2px', paddingBottom: '2px' }}
                                                        alt={player ? (player.player_full_name ? player.player_full_name : 'player') : 'player'}
                                                        src={
                                                            player.player_image
                                                                ? player.player_image.includes(process.env.REACT_APP_S3_URI)
                                                                    ? player.player_image
                                                                    : process.env.REACT_APP_S3_URI + player.player_image
                                                                : PLAYER_ICON_DEFAULT
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell key={`${player.player_id}-${index}-1`} sx={{ width: '190px' }}>
                                                    <Box sx={{ paddingLeft: '3px', width: '100%', overflow: 'hidden', cursor: 'pointer' }} onClick={handleShowMenu(player)}>
                                                        <p className="bold-text text-nowrap" style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>{player?.player_full_name}</p>
                                                        <p className="normal-text">
                                                            #{player?.player_jersey_number < 999 ? player?.player_jersey_number : 0} {player?.player_position_name ?? ''}
                                                        </p>
                                                        <p className="normal-text" style={{ color: '#666', fontSize: '11px' }}>{player?.team_name ?? ''}</p>
                                                    </Box>
                                                </TableCell>
                                                {headCells.map((cell, cId) => (
                                                    <TableCell
                                                        key={`${cell.id}-${index}-${cId}`}
                                                        align="center"
                                                        sx={{ cursor: cell.action !== '' && player[cell.id] > 0 ? 'pointer' : 'default', px: '4px' }}
                                                        onClick={handleDisplayStatDialogFromCell(cell, player)}
                                                        onContextMenu={handleExportPlayerTags(cell, player)}
                                                    >
                                                        <p className="normal-text">
                                                            {cell.id === 'pass_success_rate'
                                                                ? player[cell.id] !== null && player[cell.id] !== undefined
                                                                    ? `${Number(player[cell.id]).toFixed(1)}%`
                                                                    : '-'
                                                                : player[cell.id] ?? '-'}
                                                        </p>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <PlayerEditDialog t={t} open={editOpen} onClose={() => setEditOpen(false)} player={editPlayer} onSave={updatePlayer} />
                            <PlayerStatDialog
                                open={statOpen}
                                t={t}
                                onClose={() => setStatOpen(false)}
                                player={playerStat}
                                games={gameList}
                                gameIds={gameList.map((item) => item.id)}
                                updatePlayer={updatePlayer}
                                page="yes"
                                statsData={allPlayersStatsUnified[seasonFilter === 'none' ? 'all' : seasonFilter?.name] || []}
                                initialState={playerStat}
                            />
                            <GameExportToEdits t={t} open={exportOpen} onClose={() => setExportOpen(false)} tagList={playData} isTeams={false} />
                            <PlayersGamesDialog t={t} open={gamesOpen} onClose={() => setGamesOpen(false)} list={playerGames} playerName={playerStat?.player_full_name ?? ''} />
                            <TeamStatsVideoPlayer t={t} open={videoOpen} onClose={() => setVideoOpen(false)} video_url={videoGameList} tagList={videoData} />
                            <Popover
                                id={menuPopoverId}
                                open={menuPopoverOpen}
                                anchorEl={menuAnchorEl}
                                onClose={() => setMenuAnchorEl(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                sx={{ '& .MuiPopover-paper': { width: '220px', borderRadius: '12px', border: '1px solid #E8E8E8' } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', cursor: 'pointer' }} onClick={() => handleDisplayStats()}>
                                    <QueryStatsIcon />
                                    <p className="menu-item">{t('Accumulated Stats')}</p>
                                </Box>
                                <Divider sx={{ width: '100%' }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', cursor: 'pointer' }} onClick={() => handleDisplayGames()}>
                                    <SportsSoccerIcon />
                                    <p className="menu-item">{t('Game By Game')}</p>
                                </Box>
                                <Divider sx={{ width: '100%' }} />
                                <Box
                                    sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', cursor: 'pointer' }}
                                    onClick={handleEditClick}
                                >
                                    <EditIcon />
                                    <p className="menu-item">{editLoading ? t('Loading...') : t('Edit Player')}</p>
                                </Box>
                                <Divider sx={{ width: '100%' }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', cursor: 'pointer' }} onClick={() => handleCopyLink()} hidden={isShowShareIcon}>
                                    <ShareIcon />
                                    <p className="menu-item">{t('Share')}</p>
                                </Box>
                            </Popover>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default Players;
